import { ReactNode, useMemo, useState, useEffect, useRef } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
  ColumnDef,
  SortingState,
  VisibilityState,
  RowSelectionState,
  functionalUpdate,
  OnChangeFn,
} from '@tanstack/react-table';
import MobileCard from './MobileCard';
import Card from './Card';

export interface Column<T> {
  header: string;
  accessor: keyof T | ((row: T) => ReactNode);
  mobileLabel?: string;
  enableSorting?: boolean;
  cellRenderer?: (row: T) => ReactNode;
  sortField?: string;
}

export function getTableColumnId<T extends Record<string, any>>(col: Column<T>): string {
  if (typeof col.accessor === 'string') return col.accessor as string;
  return col.header.toLowerCase().replace(/\s+/g, '_');
}

export function resolveServerSortField<T extends Record<string, any>>(
  columns: Column<T>[],
  columnId: string | undefined
): string | undefined {
  if (!columnId) return undefined;
  const col = columns.find((c) => getTableColumnId(c) === columnId);
  return col?.sortField ?? columnId;
}

interface TableProps<T extends Record<string, any>> {
  columns: Column<T>[];
  data: T[];
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
  enableSorting?: boolean;
  enablePagination?: boolean;
  enableRowSelection?: boolean;
  enableColumnVisibility?: boolean;
  pageSize?: number;
  total?: number | null;
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
}

export default function Table<T extends Record<string, any>>({
  columns,
  data,
  onRowClick,
  emptyMessage = 'No data available',
  enableSorting = true,
  enablePagination = true,
  enableRowSelection = false,
  enableColumnVisibility = true,
  pageSize = 10,
  total,
  sorting: sortingControlled,
  onSortingChange,
}: TableProps<T>) {
  const [sortingInternal, setSortingInternal] = useState<SortingState>([]);
  const sorting = sortingControlled ?? sortingInternal;
  const isServerSort = Boolean(enableSorting && onSortingChange);
  const setSortingCombined: OnChangeFn<SortingState> = (updater) => {
    const next = functionalUpdate(updater, sorting);
    onSortingChange?.(next);
    if (sortingControlled === undefined) {
      setSortingInternal(next);
    }
  };
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [showColumnVisibility, setShowColumnVisibility] = useState(false);
  const columnVisibilityRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (columnVisibilityRef.current && !columnVisibilityRef.current.contains(event.target as Node)) {
        setShowColumnVisibility(false);
      }
    };

    if (showColumnVisibility) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showColumnVisibility]);

  const tableColumns = useMemo<ColumnDef<T>[]>(() => {
    const cols = columns.map((col) => {
      const id = getTableColumnId(col);
      const baseColumn: ColumnDef<T> = {
        id,
        header: col.header,
        enableSorting: col.enableSorting !== false && enableSorting,
        cell: ({ row }) => {
          if (col.cellRenderer) {
            return <div>{col.cellRenderer(row.original)}</div>;
          }
          let value: ReactNode;
          if (typeof col.accessor === 'function') {
            value = col.accessor(row.original);
          } else {
            value = row.getValue(col.accessor as string) as ReactNode;
          }
          return <div>{value}</div>;
        },
      };
      
      if (typeof col.accessor === 'string') {
        (baseColumn as any).accessorKey = col.accessor;
      } else if (typeof col.accessor === 'function') {
        const accessorFn = col.accessor;
        (baseColumn as any).accessorFn = (row: T) => {
          const result = accessorFn(row);
          if (typeof result === 'object' && result !== null && 'props' in result) {
            const text = (result as any).props?.children || '';
            return typeof text === 'string' ? text : String(result);
          }
          return typeof result === 'string' || typeof result === 'number' ? result : String(result);
        };
      }
      
      return baseColumn;
    });

    if (enableRowSelection) {
      return [
        {
          id: 'select',
          header: ({ table }) => (
            <input
              type="checkbox"
              checked={table.getIsAllRowsSelected()}
              onChange={table.getToggleAllRowsSelectedHandler()}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
          ),
          cell: ({ row }) => (
            <input
              type="checkbox"
              checked={row.getIsSelected()}
              onChange={row.getToggleSelectedHandler()}
              onClick={(e) => e.stopPropagation()}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
          ),
          enableSorting: false,
        } as ColumnDef<T>,
        ...cols,
      ];
    }

    return cols;
  }, [columns, enableSorting, enableRowSelection]);

  const table = useReactTable({
    data,
    columns: tableColumns,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
    },
    onSortingChange: setSortingCombined,
    manualSorting: isServerSort,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    enableRowSelection: enableRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: enableSorting && !isServerSort ? getSortedRowModel() : undefined,
    getPaginationRowModel: enablePagination ? getPaginationRowModel() : undefined,
    initialState: {
      pagination: {
        pageSize,
      },
    },
  });

  if (data.length === 0) {
    return (
      <Card>
        <div className="text-center py-12">
          <svg className="mx-auto h-16 w-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
          </svg>
          <p className="text-lg font-semibold text-gray-700 mb-2">{emptyMessage}</p>
          <p className="text-sm text-gray-500">No data to display</p>
        </div>
      </Card>
    );
  }

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        {enableColumnVisibility && (
          <div className="relative" ref={columnVisibilityRef}>
            <button
              onClick={() => setShowColumnVisibility(!showColumnVisibility)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Columns
            </button>
            {showColumnVisibility && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-10 p-2 max-h-64 overflow-y-auto">
                {table.getAllColumns()
                  .filter((column) => column.getCanHide())
                  .map((column) => {
                    const columnDef = column.columnDef;
                    const displayName = typeof columnDef.header === 'string' 
                      ? columnDef.header 
                      : column.id.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                    return (
                      <label key={column.id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={column.getIsVisible()}
                          onChange={column.getToggleVisibilityHandler()}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">{displayName}</span>
                      </label>
                    );
                  })}
              </div>
            )}
          </div>
        )}
        {enableRowSelection && Object.keys(rowSelection).length > 0 && (
          <div className="text-sm text-gray-600">
            {Object.keys(rowSelection).length} row{Object.keys(rowSelection).length !== 1 ? 's' : ''} selected
          </div>
        )}
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto rounded-xl shadow-sm border border-gray-100">
        <table className="min-w-full bg-white rounded-xl overflow-hidden">
          <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-b border-gray-200"
                  >
                    {header.isPlaceholder ? null : (
                      <div
                        className={`flex items-center gap-2 ${
                          header.column.getCanSort() ? 'cursor-pointer select-none hover:text-blue-600' : ''
                        }`}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getCanSort() && (
                          <span className="text-gray-400">
                            {{
                              asc: '↑',
                              desc: '↓',
                            }[header.column.getIsSorted() as string] ?? '⇅'}
                          </span>
                        )}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-gray-100">
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                onClick={() => onRowClick?.(row.original)}
                className={`${
                  onRowClick ? 'cursor-pointer hover:bg-blue-50/50 transition-colors duration-150' : ''
                } ${
                  enableRowSelection && row.getIsSelected() ? 'bg-blue-50' : ''
                }`}
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden">
        {table.getRowModel().rows.map((row) => (
          <MobileCard
            key={row.id}
            onClick={() => onRowClick?.(row.original)}
          >
            <div className="space-y-2">
              {columns.map((column, colIndex) => {
                const label = column.mobileLabel || column.header;
                const value = typeof column.accessor === 'function'
                  ? column.accessor(row.original)
                  : row.original[column.accessor as string];
                
                return (
                  <div key={colIndex} className="flex justify-between items-start">
                    <span className="font-semibold text-gray-700 text-sm">{label}:</span>
                    <span className="text-gray-900 text-sm text-right flex-1 ml-2">{value as ReactNode}</span>
                  </div>
                );
              })}
            </div>
          </MobileCard>
        ))}
      </div>

      {/* Pagination */}
      {enablePagination && (table.getPageCount() > 1 || (total !== null && total !== undefined && total > 0)) && (
        <div className="mt-4 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              {'<<'}
            </button>
            <button
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              {'<'}
            </button>
            <span className="text-sm text-gray-700">
              Page {table.getState().pagination.pageIndex + 1} of{' '}
              {total !== null && total !== undefined
                ? Math.ceil(total / table.getState().pagination.pageSize)
                : table.getPageCount()}
              {total !== null && total !== undefined && (
                <span className="text-gray-500 ml-1">
                  ({total.toLocaleString()} total)
                </span>
              )}
            </span>
            <button
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              {'>'}
            </button>
            <button
              onClick={() => {
                const maxPage = total !== null && total !== undefined
                  ? Math.ceil(total / table.getState().pagination.pageSize) - 1
                  : table.getPageCount() - 1;
                table.setPageIndex(maxPage);
              }}
              disabled={!table.getCanNextPage()}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              {'>>'}
            </button>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">Show:</span>
            <select
              value={table.getState().pagination.pageSize}
              onChange={(e) => {
                table.setPageSize(Number(e.target.value));
              }}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md"
            >
              {[10, 20, 30, 50, 100].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  {pageSize}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
    </>
  );
}
